import { useEffect, useState } from "react";
import { Download, Eye, FileText, Trash2 } from "lucide-react";

import { api } from "@/api/client";
import PDFViewerModal from "@/components/PDFViewerModal";

/**
 * A file that lives in a private bucket, shown with a way to look at it.
 *
 * The record stores an object path, not a URL - so putting the stored value
 * straight into `src` or `href` gives the browser nothing to fetch, which is
 * exactly what the broken-image icon on the signature was. Every stored file
 * needs a signed URL minted for it first, and that URL expires, so it is
 * fetched when the component mounts rather than saved anywhere.
 */
export default function StoredFile({
  path,
  label,
  contentType = "application/pdf",
  onRemove,
  removeLabel = "Remove",
}) {
  const [url, setUrl] = useState(null);
  const [filename, setFilename] = useState("");
  const [error, setError] = useState("");
  const [viewing, setViewing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const isImage = contentType.startsWith("image/");

  useEffect(() => {
    let cancelled = false;

    if (!path) {
      setUrl(null);
      return undefined;
    }

    setError("");
    setUrl(null);

    api
      .fileUrl(path)
      .then((result) => {
        if (cancelled) return;
        setUrl(result.url);
        setFilename(result.filename || label);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Could not load this file.");
      });

    return () => {
      cancelled = true;
    };
  }, [path, label]);

  const handleDownload = async () => {
    setDownloading(true);
    setError("");
    try {
      // A separate URL from the viewing one: storage has to set
      // Content-Disposition for the file to save rather than open, and that is
      // baked into the signature.
      const { url: attachment } = await api.fileUrl(path, { download: filename || label });
      window.location.href = attachment;
    } catch (err) {
      setError(err.message || "Could not download this file.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="stored-file">
        <div className="stored-file-thumb">
          {isImage && url ? (
            <img src={url} alt={label} />
          ) : (
            <FileText size={24} style={{ color: "#16a34a" }} />
          )}
        </div>

        <div className="stored-file-meta">
          <div className="stored-file-name">{label}</div>
          <div className="muted" style={{ fontSize: "0.75rem" }}>
            {error ? <span style={{ color: "#b91c1c" }}>{error}</span> : filename || "\u00a0"}
          </div>
        </div>

        <div className="stored-file-actions">
          <button
            className="btn btn-sm btn-outline"
            onClick={() => setViewing(true)}
            disabled={!url}
            title={url ? "" : "Preparing the file..."}
          >
            <Eye size={15} />
            View
          </button>
          <button
            className="btn btn-sm btn-outline"
            onClick={handleDownload}
            disabled={!url || downloading}
          >
            <Download size={15} />
            {downloading ? "..." : "Download"}
          </button>
          {onRemove && (
            <button className="btn btn-sm btn-outline stored-file-remove" onClick={onRemove}>
              <Trash2 size={15} />
              {removeLabel}
            </button>
          )}
        </div>
      </div>

      {viewing && url && (
        <PDFViewerModal
          pdfUrl={url}
          title={filename || label}
          contentType={contentType}
          onClose={() => setViewing(false)}
        />
      )}
    </>
  );
}
